import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MoreMenu, { swatch } from './MoreMenu';
import { Icon } from './icons';
import TemplateEditor from './TemplateEditor';
import TemplateApply from './TemplateApply';
import {
  deleteTemplate, draftFromList, duplicateTemplate, errorText, loadTemplate, loadTemplates, notReady,
  restoreTemplate, usageLabel
} from '../../lib/todoTemplates';
import './TodoTemplates.css';

/**
 * TEMPLATES (migration 071): the gallery, and the way into the editor and
 * the Apply dialog.
 *
 * Gordon, 22 October: Todoist's and Planner's templates for an accounting
 * firm - Month-end close, VAT return, New client onboarding, Audit file
 * completion. The firm-wide ones first (everybody at NCMB may use them),
 * then the person's own; each card says what it is, how its days are
 * counted, the parts people play in it and how often it has been used. "Use
 * template" opens the Apply dialog (TemplateApply.js); Edit, the editor
 * (TemplateEditor.js), which takes the place of the gallery until it is
 * saved or left.
 *
 * Staff only - TodoApp draws none of this for a contractor, and the server
 * answers a contractor 403 anyway. A delete is undone from the toast, and
 * "Recently deleted" at the foot brings back anything older.
 *
 * `intent` is how the list ⋯'s "Save as template…" arrives: { kind:
 * 'fromList', listId, key } opens the editor on a draft made from that
 * list. `onApplied` is told what the apply made, so the page can open it.
 */
export default function TodoTemplates({
  lists, labels, people, needPeople, selfKey, isAdmin, intent, onIntentDone, onApplied, showToast, phone
}) {
  const [all, setAll] = useState(null);         // null while loading
  const [deleted, setDeleted] = useState(null);  // null until asked for
  const [showDeleted, setShowDeleted] = useState(false);
  const [state, setState] = useState('ok');      // 'ok' | 'unset' | 'error'
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);  // { template, draftNote? }
  const [applying, setApplying] = useState(null); // a template, with its steps

  const load = useCallback(async () => {
    try {
      const r = await loadTemplates();
      setAll(Array.isArray(r?.templates) ? r.templates : []);
      setState('ok');
    } catch (err) {
      if (notReady(err)) { setState('unset'); setAll([]); return; }
      setState('error');
      setError(errorText(err, 'Could not load the templates'));
      setAll([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadDeleted = useCallback(async () => {
    setDeleted(null);
    try {
      const r = await loadTemplates({ deleted: true });
      setDeleted(Array.isArray(r?.templates) ? r.templates : []);
    } catch (err) {
      setDeleted([]);
      setError(errorText(err, 'Could not load the deleted templates'));
    }
  }, []);

  /** Something that changes a template: busy while it runs, its error shown. */
  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try { return await fn(); } catch (err) {
      setError(errorText(err));
      return null;
    } finally { setBusy(false); }
  };

  // "Save as template…" from a list's ⋯: a draft from the list, in the editor.
  useEffect(() => {
    if (!intent || intent.kind !== 'fromList') return;
    let live = true;
    (async () => {
      const draft = await act(() => draftFromList(intent.listId));
      if (!live) return;
      if (draft) {
        setEditing({
          template: { ...draft, id: null },
          note: draft.roles?.length
            ? `Made from “${draft.from_list?.name}”. The people its to-dos were given to are roles named after them - rename each to the part it is (Preparer, Reviewer), then save.`
            : `Made from “${draft.from_list?.name}”. Check the steps, then save.`
        });
      }
      if (onIntentDone) onIntentDone();
    })();
    return () => { live = false; };
    // Once per intent: `key` changes when the menu is used again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent?.key]);

  const open = async (t, then) => {
    const full = t.steps ? t : await act(() => loadTemplate(t.id));
    if (full) then(full);
  };

  const remove = (t) => act(async () => {
    await deleteTemplate(t.id);
    setAll((prev) => (prev || []).filter((x) => x.id !== t.id));
    setDeleted(null);
    if (showDeleted) loadDeleted();
    showToast(`Deleted “${t.name}”`, {
      undo: () => act(async () => { await restoreTemplate(t.id); await load(); setDeleted(null); })
    });
  });

  const duplicate = (t) => act(async () => {
    const copy = await duplicateTemplate(t.id);
    await load();
    showToast(`Made “${copy.name}” - yours to change`);
    setEditing({ template: copy });
  });

  const restore = (t) => act(async () => {
    await restoreTemplate(t.id);
    setDeleted((prev) => (prev || []).filter((x) => x.id !== t.id));
    await load();
    showToast(`“${t.name}” is back`);
  });

  const q = query.trim().toLowerCase();
  const shown = useMemo(() => (all || []).filter((t) => !q
    || [t.name, t.description, ...(t.roles || []), ...(t.first_steps || [])]
      .some((x) => String(x || '').toLowerCase().includes(q))), [all, q]);
  const firm = shown.filter((t) => t.visibility === 'firm');
  const mine = shown.filter((t) => t.visibility !== 'firm');

  /* ---------------------------------------------------------- the editor */

  if (editing) {
    return (
      <TemplateEditor key={editing.template.id || 'new'} template={editing.template} note={editing.note}
                      isAdmin={isAdmin} labels={labels} phone={phone}
                      onCancel={() => setEditing(null)}
                      onSaved={(t, isNew) => {
                        setEditing(null);
                        load();
                        showToast(isNew ? `Saved “${t.name}”` : `Saved changes to “${t.name}”`);
                      }} />
    );
  }

  /* --------------------------------------------------------- the gallery */

  const card = (t) => {
    const menu = [
      { label: t.can_edit ? 'Edit' : 'View steps', icon: 'edit', onClick: () => open(t, (full) => setEditing({ template: full })) },
      { label: 'Duplicate', icon: 'copy', onClick: () => duplicate(t) },
      t.can_edit && { sep: true },
      t.can_edit && { label: 'Delete', icon: 'trash', danger: true, onClick: () => remove(t) }
    ];
    return (
      <li key={t.id} className={`tpl-card ${swatch(t.color)}`} data-template={t.id}>
        <div className="tpl-card-top">
          <span className="tpl-tile" aria-hidden="true"><Icon name={t.icon || 'template'} size={20} /></span>
          <div className="tpl-card-name">
            <h4>{t.name}</h4>
            <p className="tpl-card-kind">
              {t.visibility === 'firm' ? (t.system ? 'Firm-wide · NCMB' : `Firm-wide · ${t.owner_name || 'staff'}`) : 'Just you'}
            </p>
          </div>
          <MoreMenu label={`More for template "${t.name}"`} disabled={busy} items={menu} />
        </div>
        {t.description && <p className="tpl-card-desc">{t.description}</p>}
        <p className="tpl-card-meta">
          <span><Icon name="list" size={13} />{t.step_count} {t.step_count === 1 ? 'step' : 'steps'}</span>
          <span><Icon name="calendar" size={13} />
            {t.day_kind === 'working' ? 'Working days' : 'Calendar days'}, {t.anchor === 'due' ? 'back from the due date' : 'from the start'}
          </span>
        </p>
        {(t.roles || []).length > 0 && (
          <ul className="tpl-roles" aria-label="Roles">
            {t.roles.map((r) => <li key={r} className="tpl-role"><Icon name="person" size={12} />{r}</li>)}
          </ul>
        )}
        <div className="tpl-card-foot">
          <span className="tpl-usage">{usageLabel(t)}</span>
          <button type="button" className="btn btn-primary btn-small tpl-use" disabled={busy}
                  aria-label={`Use template "${t.name}"`}
                  onClick={() => { needPeople(); open(t, (full) => setApplying(full)); }}>Use template</button>
        </div>
      </li>
    );
  };

  const grid = (title, rows, empty) => (
    <section className="tpl-group" aria-label={title}>
      <h3 className="tpl-group-head">{title}<span className="tpl-group-n">{rows.length || ''}</span></h3>
      {rows.length ? <ul className="tpl-grid">{rows.map(card)}</ul> : <p className="tpl-quiet">{empty}</p>}
    </section>
  );

  return (
    <div className="tpl">
      <div className="tda-head tpl-head">
        <h2 className="tda-title">
          <span className="tda-dot is-icon" aria-hidden="true"><Icon name="template" /></span>
          <span className="tda-title-text">Templates</span>
        </h2>
        {state === 'ok' && (
          <>
            <span className="ctd-search-box tpl-search">
              <Icon name="search" size={14} className="ctd-search-ic" />
              <input type="search" className="ctd-search" value={query} placeholder="Search templates"
                     aria-label="Search templates" onChange={(e) => setQuery(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }} />
            </span>
            <button type="button" className="btn btn-primary btn-small tpl-new" disabled={busy}
                    onClick={() => setEditing({ template: null })}>
              <Icon name="plus" size={14} />New template
            </button>
          </>
        )}
      </div>

      {error && <p className="ctd-error" role="alert">{error}</p>}

      {state === 'unset' ? (
        <div className="ctd-empty tda-unset" role="status">
          <p className="tda-unset-title">Templates are not set up on this server yet.</p>
          <p>They arrive as soon as the server has had its update (migration 071).</p>
        </div>
      ) : all === null ? (
        <p className="ctd-quiet">Loading…</p>
      ) : (
        <>
          <p className="tpl-intro">
            A list of steps you do again and again. Use one with a date and the people who play each part,
            and every step is made as a to-do with its own due date and person.
          </p>
          {q && !shown.length ? (
            <div className="ctd-empty ctd-empty-state" role="status">
              <span className="ctd-empty-ic" aria-hidden="true"><Icon name="search" size={28} /></span>
              <p className="ctd-empty-title">No matches</p>
              <p className="ctd-empty-text">No template matches “{query.trim()}”.</p>
            </div>
          ) : (
            <>
              {grid('Firm-wide', firm, 'No firm-wide templates yet.')}
              {grid('Mine', mine, q ? 'None of yours match.'
                : 'Templates only you can see go here. Make one, duplicate one of the firm’s, or save a list as one from its ⋯.')}
            </>
          )}
          <button type="button" className="tda-add-line tpl-deleted-toggle" aria-expanded={showDeleted}
                  onClick={() => { const next = !showDeleted; setShowDeleted(next); if (next) loadDeleted(); }}>
            {showDeleted ? 'Hide recently deleted' : 'Recently deleted'}
          </button>
          {showDeleted && (
            <ul className="tpl-deleted" aria-label="Recently deleted templates">
              {deleted === null ? <li className="tpl-quiet">Loading…</li>
                : deleted.length === 0 ? <li className="tpl-quiet">Nothing deleted.</li>
                  : deleted.map((t) => (
                    <li key={t.id} className={`tpl-deleted-row ${swatch(t.color)}`} data-deleted-template={t.id}>
                      <span className="tda-dot" aria-hidden="true" />
                      <span className="tpl-deleted-name">{t.name}</span>
                      <button type="button" className="tda-add-line" disabled={busy}
                              aria-label={`Restore "${t.name}"`} onClick={() => restore(t)}>
                        <Icon name="restore" size={14} />Restore
                      </button>
                    </li>
                  ))}
            </ul>
          )}
        </>
      )}

      {applying && (
        <TemplateApply template={applying} lists={lists} people={people} selfKey={selfKey}
                       onClose={() => setApplying(null)}
                       onApplied={(r) => { setApplying(null); load(); onApplied(r); }} />
      )}
    </div>
  );
}
